import {
  PromoPlanType,
  PromoStatus,
  PromoTermType,
  PromoTermUnit,
  PromoWeekday,
} from "../../../apis/requests/promos/types";
import {ChipVariant} from "../../templates/templateDisplay";

export const PROMO_STATUSES = ["draft", "active", "ended"] as const satisfies readonly PromoStatus[];
export type SettablePromoStatus = (typeof PROMO_STATUSES)[number];
export const SETTABLE_STATUS_BY_STATUS: Record<PromoStatus, SettablePromoStatus> = {
  draft: "draft",
  active: "active",
  ended: "ended",
  archived: "draft",
};

export const PROMO_KEY_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)*$/;
export const PROMO_KEY_MAX_LENGTH = 64;
export const PROMO_TERM_TYPES = [
  "stack_on_current_discount",
  "set_one_time_discount",
  "plan_length",
] as const satisfies readonly PromoTermType[];
export const DISCOUNT_TERM_TYPES = new Set<PromoTermType>(["stack_on_current_discount", "set_one_time_discount"]);
export const PROMO_TERM_UNITS = ["percent", "installments", "sa_max"] as const satisfies readonly PromoTermUnit[];

export const ALLOWED_UNITS_BY_TERM_TYPE: Record<PromoTermType, PromoTermUnit[]> = {
  stack_on_current_discount: ["percent"],
  set_one_time_discount: ["percent"],
  plan_length: ["installments", "sa_max"],
};
export const MAX_PERCENT = 100;
export const UNITS_WITHOUT_AMOUNT = new Set<PromoTermUnit>(["sa_max"]);

export const PROMO_PLAN_TYPES = ["installment_plans", "one_time_payment"] as const satisfies readonly PromoPlanType[];

export const ALL_PLANS = "all_plans";
export const APPLIES_TO_OPTIONS = [ALL_PLANS, ...PROMO_PLAN_TYPES] as const;
export type AppliesToOption = (typeof APPLIES_TO_OPTIONS)[number];

export const ALLOWED_PLAN_TYPES_BY_TERM_TYPE: Record<PromoTermType, PromoPlanType[]> = {
  stack_on_current_discount: [...PROMO_PLAN_TYPES],
  set_one_time_discount: ["one_time_payment"],
  plan_length: ["installment_plans"],
};

export const PROMO_WINDOWS = ["72h", "7d", "14d", "custom"] as const;
export type PromoWindow = (typeof PROMO_WINDOWS)[number];

export const START_MODES = ["date", "weekday"] as const;
export type StartMode = (typeof START_MODES)[number];

export const PROMO_WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const satisfies readonly PromoWeekday[];

export const CADENCE_UNITS = ["days", "weeks", "months"] as const;
export type CadenceUnit = (typeof CADENCE_UNITS)[number];

export const WINDOW_DURATION_HOURS: Record<Exclude<PromoWindow, "custom">, number> = {
  "72h": 72,
  "7d": 168,
  "14d": 336,
};

export const CADENCE_UNIT_DAYS: Record<CadenceUnit, number> = {
  days: 1,
  weeks: 7,
  months: 30,
};

export const STATUS_DISPLAY: Record<PromoStatus, {label: string; variant: ChipVariant}> = {
  draft: {label: "Draft", variant: "inherit"},
  active: {label: "Active", variant: "success"},
  ended: {label: "Ended", variant: "secondary"},
  archived: {label: "Archived", variant: "inherit"},
};

export const TERM_TYPE_LABELS: Record<PromoTermType, string> = {
  stack_on_current_discount: "Extra discount",
  set_one_time_discount: "Set one-time discount (max by SA)",
  plan_length: "Plan length",
};

export const TERM_TYPE_SHORT_LABELS: Record<PromoTermType, string> = {
  stack_on_current_discount: "Extra",
  set_one_time_discount: "Set",
  plan_length: "Length",
};

export const PLAN_TYPE_LABELS: Record<PromoPlanType, string> = {
  installment_plans: "Installment plans",
  one_time_payment: "One-time plans",
};

export const APPLIES_TO_LABELS: Record<AppliesToOption, string> = {
  all_plans: "All plans",
  ...PLAN_TYPE_LABELS,
};

export const SELECTABLE_UNIT_LABELS: Record<PromoTermUnit, string> = {
  percent: "%",
  installments: "Extra installments",
  sa_max: "Max SA",
};

export const FIXED_UNIT_LABELS: Partial<Record<PromoTermType, string>> = {
  stack_on_current_discount: "%",
  set_one_time_discount: "%",
};

export const TERM_PLAN_TYPE_REPEATED = "Each term can apply to a plan type only once";
export const SINGLE_ONE_TIME_DISCOUNT = "Pick either an extra or a set one-time discount, not both";

export const WINDOW_LABELS: Record<PromoWindow, string> = {
  "72h": "3 days",
  "7d": "7 days",
  "14d": "14 days",
  custom: "Custom",
};

export const START_MODE_LABELS: Record<StartMode, string> = {
  date: "On a date",
  weekday: "On a specific day",
};

export const WEEKDAY_SHORT_LABELS: Record<PromoWeekday, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export const WEEKDAY_LABELS: Record<PromoWeekday, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const CADENCE_UNIT_LABELS: Record<CadenceUnit, string> = {
  days: "days",
  weeks: "weeks",
  months: "months",
};

export const BOARD_TABS = ["all", ...PROMO_STATUSES] as const;
export type BoardTab = (typeof BOARD_TABS)[number];
